import { User } from '@shiftly/shared/src/schema';
import { supabase } from '../supabase';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';

// Simplified schema for fan_gain_assignments
export interface FanGainAssignment {
  id: string;
  user_id: string;
  model_id: string;
  index: number;
  created_at: string;
  updated_at: string;
}

export interface CreateAssignmentData {
  user_id: string;
  model_id: string;
}

export interface AssignModelData {
  user_id: string;
  model_id: string;
}

export interface FanGainUserOrder {
  id: string;
  user_id: string;
  order_index: number;
  created_at: string;
  updated_at: string;
}

const SupabaseUserModelAssignmentService = {
  /**
   * Create a new user-model assignment
   */
  async createAssignment(data: CreateAssignmentData): Promise<string> {
    try {
      // Get the highest index for this user to place new assignment at the end
      const existingAssignments = await this.getAssignmentsByUser(data.user_id);
      const maxIndex = existingAssignments.length > 0 
        ? Math.max(...existingAssignments.map(a => a.index))
        : -1;

      const { data: result, error } = await supabase
        .from('fan_gain_assignments')
        .insert({
          user_id: data.user_id,
          model_id: data.model_id,
          index: maxIndex + 1
        })
        .select('id')
        .single();

      if (error) throw error;
      return result.id;
    } catch (error) {
      console.error('Error creating assignment:', error);
      throw error;
    }
  },

  /**
   * Get all assignments
   */
  async getAllAssignments(): Promise<FanGainAssignment[]> {
    try {
      const { data, error } = await supabase
        .from('fan_gain_assignments')
        .select('*')
        .order('index', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting all assignments:', error);
      throw error;
    }
  },

  /**
   * Get assignments by user ID
   */
  async getAssignmentsByUser(user_id: string): Promise<FanGainAssignment[]> {
    try {
      const { data, error } = await supabase
        .from('fan_gain_assignments')
        .select('*')
        .eq('user_id', user_id)
        .order('index', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting assignments by user:', error);
      throw error;
    }
  },

  /**
   * Get assignments by model ID
   */
  async getAssignmentsByModel(model_id: string): Promise<FanGainAssignment[]> {
    try {
      const { data, error } = await supabase
        .from('fan_gain_assignments')
        .select('*')
        .eq('model_id', model_id)
        .order('index', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting assignments by model:', error);
      throw error;
    }
  },

  /**
   * Assign a model to a user
   */
  async assignModelToUser(data: AssignModelData): Promise<void> {
    try {
      // Check if assignment already exists
      const existingAssignments = await this.getAssignmentsByUser(data.user_id);
      const existingAssignment = existingAssignments.find(a => a.model_id === data.model_id);
      
      if (existingAssignment) {
        throw new Error('Model is already assigned to this user');
      }

      await this.createAssignment(data);
    } catch (error) {
      console.error('Error assigning model to user:', error);
      throw error;
    }
  },

  /**
   * Remove a model from a user
   */
  async removeModelFromUser(user_id: string, model_id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('fan_gain_assignments')
        .delete()
        .eq('user_id', user_id)
        .eq('model_id', model_id);

      if (error) throw error;

      // Reorder remaining assignments for this user
      await this.reorderUserAssignments(user_id);
    } catch (error) {
      console.error('Error removing model from user:', error);
      throw error;
    }
  },

  /**
   * Move model from one user to another
   */
  async moveModelBetweenUsers(
    fromuser_id: string,
    touser_id: string,
    model_id: string
  ): Promise<void> {
    try {
      // Check if model is assigned to the source user
      const fromUserAssignments = await this.getAssignmentsByUser(fromuser_id);
      const modelAssignment = fromUserAssignments.find(a => a.model_id === model_id);
      
      if (!modelAssignment) {
        throw new Error('Model is not assigned to the source user');
      }

      // Check if model is already assigned to the destination user
      const toUserAssignments = await this.getAssignmentsByUser(touser_id);
      const existingAssignment = toUserAssignments.find(a => a.model_id === model_id);
      
      if (existingAssignment) {
        throw new Error('Model is already assigned to the destination user');
      }

      // Remove from source user and add to destination user
      await this.removeModelFromUser(fromuser_id, model_id);
      await this.assignModelToUser({ user_id: touser_id, model_id });
    } catch (error) {
      console.error('Error moving model between users:', error);
      throw error;
    }
  },

  /**
   * Delete an assignment by ID
   */
  async deleteAssignment(assignmentId: string): Promise<void> {
    try {
      // Get the assignment first to know which user to reorder
      const { data: assignment, error: fetchError } = await supabase
        .from('fan_gain_assignments')
        .select('user_id')
        .eq('id', assignmentId)
        .single();

      if (fetchError) throw fetchError;

      const { error } = await supabase
        .from('fan_gain_assignments')
        .delete()
        .eq('id', assignmentId);

      if (error) throw error;

      // Reorder remaining assignments for this user
      if (assignment) {
        await this.reorderUserAssignments(assignment.user_id);
      }
    } catch (error) {
      console.error('Error deleting assignment:', error);
      throw error;
    }
  },

  /**
   * Update the order of assignments for a specific user
   */
  async updateUserAssignmentOrder(user_id: string, assignmentIds: string[]): Promise<void> {
    try {
      // Update each assignment with its new index
      const updates = assignmentIds.map((id, index) => 
        supabase
          .from('fan_gain_assignments')
          .update({ 
            index: index,
            updated_at: DateTime.utc()
          })
          .eq('id', id)
          .eq('user_id', user_id)
      );

      await Promise.all(updates);
    } catch (error) {
      console.error('Error updating assignment order:', error);
      throw error;
    }
  },

  /**
   * Reorder assignments for a user (used after deletions)
   */
  async reorderUserAssignments(user_id: string): Promise<void> {
    try {
      const assignments = await this.getAssignmentsByUser(user_id);
      const assignmentIds = assignments.map(a => a.id);
      await this.updateUserAssignmentOrder(user_id, assignmentIds);
    } catch (error) {
      console.error('Error reordering user assignments:', error);
      throw error;
    }
  },

  /**
   * Get assignment by user and model
   */
  async getAssignmentByUserAndModel(user_id: string, model_id: string): Promise<FanGainAssignment | null> {
    try {
      const { data, error } = await supabase
        .from('fan_gain_assignments')
        .select('*')
        .eq('user_id', user_id)
        .eq('model_id', model_id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error getting assignment by user and model:', error);
      throw error;
    }
  },

  /**
   * Check if a model is assigned to a user
   */
  async isModelAssignedToUser(user_id: string, model_id: string): Promise<boolean> {
    try {
      const assignment = await this.getAssignmentByUserAndModel(user_id, model_id);
      return assignment !== null;
    } catch (error) {
      console.error('Error checking if model is assigned to user:', error);
      return false;
    }
  },

  /**
   * Get all user orders
   */
  async getAllUserOrders(): Promise<FanGainUserOrder[]> {
    try {
      const { data, error } = await supabase
        .from('fan_gain_user_order')
        .select('*')
        .order('order_index', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting all user orders:', error);
      throw error;
    }
  },

  /**
   * Get user order by user ID
   */
  async getUserOrder(user_id: string): Promise<FanGainUserOrder | null> {
    try {
      const { data, error } = await supabase
        .from('fan_gain_user_order')
        .select('*')
        .eq('user_id', user_id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error getting user order:', error);
      throw error;
    }
  },

  /**
   * Set user order position
   */
  async setUserOrder(user_id: string, orderIndex: number): Promise<void> {
    try {
      const { error } = await supabase
        .from('fan_gain_user_order')
        .upsert({
          user_id: user_id,
          order_index: orderIndex,
          updated_at: DateTime.utc()
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error setting user order:', error);
      throw error;
    }
  },

  /**
   * Update multiple user orders at once
   */
  async updateUserOrders(userOrders: { user_id: string; orderIndex: number }[]): Promise<void> {
    try {
      // Step 1: Set all order indices to negative values to avoid conflicts
      const tempUpdates = userOrders.map(({ user_id }, index) => ({
        user_id: user_id,
        order_index: -(index + 1), // Use negative indices as temporary values
        updated_at: DateTime.utc()
      }));

      const { error: tempError } = await supabase
        .from('fan_gain_user_order')
        .upsert(tempUpdates, { 
          onConflict: 'user_id',
          ignoreDuplicates: false 
        });

      if (tempError) throw tempError;

      // Step 2: Update to the actual order indices
      const finalUpdates = userOrders.map(({ user_id, orderIndex }) => ({
        user_id: user_id,
        order_index: orderIndex,
        updated_at: DateTime.utc()
      }));

      const { error: finalError } = await supabase
        .from('fan_gain_user_order')
        .upsert(finalUpdates, { 
          onConflict: 'user_id',
          ignoreDuplicates: false 
        });

      if (finalError) throw finalError;
    } catch (error) {
      console.error('Error updating user orders:', error);
      throw error;
    }
  },

  /**
   * Remove user from ordering
   */
  async removeUserOrder(user_id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('fan_gain_user_order')
        .delete()
        .eq('user_id', user_id);

      if (error) throw error;

      // Reorder remaining users to fill gaps
      await this.reorderAllUsers();
    } catch (error) {
      console.error('Error removing user order:', error);
      throw error;
    }
  },

  /**
   * Reorder all users to remove gaps in ordering
   */
  async reorderAllUsers(): Promise<void> {
    try {
      const userOrders = await this.getAllUserOrders();
      const reorderedUpdates = userOrders.map((userOrder, index) => ({
        user_id: userOrder.user_id,
        orderIndex: index
      }));

      await this.updateUserOrders(reorderedUpdates);
    } catch (error) {
      console.error('Error reordering all users:', error);
      throw error;
    }
  }
};

// Query hooks
export function useAllAssignments() {
  return useQuery({
    queryKey: ['fan-gain-assignments'],
    queryFn: () => SupabaseUserModelAssignmentService.getAllAssignments(),
    staleTime: 30000,
    gcTime: 300000,
  });
}

export function useAssignmentsByUser(user_id: string) {
  return useQuery({
    queryKey: ['fan-gain-assignments', 'by-user', user_id],
    queryFn: () => SupabaseUserModelAssignmentService.getAssignmentsByUser(user_id),
    staleTime: 30000,
    gcTime: 300000,
    enabled: !!user_id
  });
}

export function useAssignmentsByModel(model_id: string) {
  return useQuery({
    queryKey: ['fan-gain-assignments', 'by-model', model_id],
    queryFn: () => SupabaseUserModelAssignmentService.getAssignmentsByModel(model_id),
    staleTime: 30000,
    gcTime: 300000,
    enabled: !!model_id
  });
}

export function useAssignmentByUserAndModel(user_id: string, model_id: string) {
  return useQuery({
    queryKey: ['fan-gain-assignment', user_id, model_id],
    queryFn: () => SupabaseUserModelAssignmentService.getAssignmentByUserAndModel(user_id, model_id),
    staleTime: 30000,
    gcTime: 300000,
    enabled: !!user_id && !!model_id
  });
}

// Mutation hooks
export function useCreateAssignment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateAssignmentData) => SupabaseUserModelAssignmentService.createAssignment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fan-gain-assignments'] });
    },
    onError: (error) => {
      console.error('Error creating assignment:', error);
    },
  });
}

export function useAssignModel() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: AssignModelData) => SupabaseUserModelAssignmentService.assignModelToUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fan-gain-assignments'] });
    },
    onError: (error) => {
      console.error('Error assigning model:', error);
    },
  });
}

export function useRemoveModel() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ user_id, model_id }: { user_id: string; model_id: string; }) => 
      SupabaseUserModelAssignmentService.removeModelFromUser(user_id, model_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fan-gain-assignments'] });
    },
    onError: (error) => {
      console.error('Error removing model:', error);
    },
  });
}

export function useMoveModel() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ 
      fromuser_id, 
      touser_id, 
      model_id 
    }: { 
      fromuser_id: string; 
      touser_id: string; 
      model_id: string; 
    }) => SupabaseUserModelAssignmentService.moveModelBetweenUsers(fromuser_id, touser_id, model_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fan-gain-assignments'] });
    },
    onError: (error) => {
      console.error('Error moving model:', error);
    },
  });
}

export function useDeleteAssignment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (assignmentId: string) => SupabaseUserModelAssignmentService.deleteAssignment(assignmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fan-gain-assignments'] });
    },
    onError: (error) => {
      console.error('Error deleting assignment:', error);
    },
  });
}

export function useUpdateAssignmentOrder() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ user_id, assignmentIds }: { user_id: string; assignmentIds: string[] }) => 
      SupabaseUserModelAssignmentService.updateUserAssignmentOrder(user_id, assignmentIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fan-gain-assignments'] });
    },
    onError: (error) => {
      console.error('Error updating assignment order:', error);
    },
  });
}

// User Order Query hooks
export function useAllUserOrders() {
  return useQuery({
    queryKey: ['fan-gain-user-orders'],
    queryFn: () => SupabaseUserModelAssignmentService.getAllUserOrders(),
    staleTime: 30000,
    gcTime: 300000,
  });
}

export function useUserOrder(user_id: string) {
  return useQuery({
    queryKey: ['fan-gain-user-order', user_id],
    queryFn: () => SupabaseUserModelAssignmentService.getUserOrder(user_id),
    staleTime: 30000,
    gcTime: 300000,
    enabled: !!user_id
  });
}

// User Order Mutation hooks
export function useSetUserOrder() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ user_id, orderIndex }: { user_id: string; orderIndex: number }) => 
      SupabaseUserModelAssignmentService.setUserOrder(user_id, orderIndex),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fan-gain-user-orders'] });
    },
    onError: (error) => {
      console.error('Error setting user order:', error);
    },
  });
}

export function useUpdateUserOrders() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (userOrders: { user_id: string; orderIndex: number }[]) => 
      SupabaseUserModelAssignmentService.updateUserOrders(userOrders),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fan-gain-user-orders'] });
    },
    onError: (error) => {
      console.error('Error updating user orders:', error);
    },
  });
}

export function useRemoveUserOrder() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (user_id: string) => SupabaseUserModelAssignmentService.removeUserOrder(user_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fan-gain-user-orders'] });
    },
    onError: (error) => {
      console.error('Error removing user order:', error);
    },
  });
}

export function useReorderAllUsers() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: () => SupabaseUserModelAssignmentService.reorderAllUsers(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fan-gain-user-orders'] });
    },
    onError: (error) => {
      console.error('Error reordering all users:', error);
    },
  });
}

export function useUserGroups(users: User[]) {
  const { data: assignments = [] } = useAllAssignments();
  const { data: userOrders = [] } = useAllUserOrders();

  return useQuery({
    queryKey: ['user-groups', assignments, userOrders, users],
    queryFn: () => {
      const userGroupsMap = new Map();

      assignments.forEach(assignment => {
        const user_id = assignment.user_id;
        
        if (!userGroupsMap.has(user_id)) {
          const user = users.find(u => u.id === user_id);
          const userOrder = userOrders.find(uo => uo.user_id === user_id);
          
          userGroupsMap.set(user_id, {
            id: user_id,
            user_id: user_id,
            userName: user?.full_name || 'Unknown User',
            assignedModels: [],
            orderIndex: userOrder?.order_index || 0
          });
        }
        
        userGroupsMap.get(user_id).assignedModels.push(assignment.model_id);
      });

      return Array.from(userGroupsMap.values()).sort((a, b) => a.orderIndex - b.orderIndex);
    },
    staleTime: 30000,
    gcTime: 300000,
    enabled: assignments.length > 0 && users.length > 0
  });
}

export default SupabaseUserModelAssignmentService;